import React, { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { RenderVirtualizedItemProps } from "./types";
import VirtualizedTableStore from "./store";

export * from "./types";

export { default as VirtualizedTableStore } from "./store";

interface VirtualizedTableProps<T> {
    data?: T[];

    prefix?: React.ReactNode;

    thead?: React.ReactNode;

    RenderRow: React.FC<RenderVirtualizedItemProps<T>>;

    renderRowProps?: any;

    onVirtualizedTableStoreChange?: (store: VirtualizedTableStore<T>) => void;
}

const VirtualizedTable = function <T>(props: VirtualizedTableProps<T>) {
    const { data, prefix, thead, RenderRow, renderRowProps = {}, onVirtualizedTableStoreChange } = props;
    const dataLength = data?.length || 0;
    const firstData = data?.[0];
    const lastData = data?.[dataLength - 1];
    const tableRef = useRef<HTMLTableElement>(null);
    const [store] = useState(
        () =>
            new VirtualizedTableStore<T>({
                getKey(data: any) {
                    return `${data.shopId}-${data.id}`;
                },
            }),
    );

    // store 是普通 TypeScript 对象，由 React 官方外部状态订阅 API 驱动更新。
    useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
    const measureFrameRef = useRef<number>();
    const pendingMeasurementElementsRef = useRef(new Set<HTMLElement>());

    useEffect(() => {
        if (onVirtualizedTableStoreChange) {
            onVirtualizedTableStoreChange(store);

            return () => {
                onVirtualizedTableStoreChange(null);
            };
        }
    }, [onVirtualizedTableStoreChange, store]);

    useLayoutEffect(() => {
        // 虚拟滚动自身更新不会触发这里，避免每次滚动窗口变化都扫描完整数据。
        store.onDataChange(data || []);
    }, [data, dataLength, firstData, lastData, store]);

    useLayoutEffect(() => {
        if (tableRef.current) {
            // 保存表格在文档中的绝对位置，把 window.scrollY 换算为表内偏移。
            store.onTableBoundingTop(window.scrollY + tableRef.current.getBoundingClientRect().top);
        }
    });

    useLayoutEffect(() => {
        const syncContainerSize = () => {
            store.handleContainerSizeChange({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };
        const syncScrollTop = () => store.onScrollTopChange(window.scrollY);
        let resizeFrame: number | undefined;
        let scrollFrame: number | undefined;

        const resize = () => {
            if (resizeFrame !== undefined) return;

            resizeFrame = window.requestAnimationFrame(() => {
                resizeFrame = undefined;
                syncContainerSize();
            });
        };
        const scroll = () => {
            if (scrollFrame !== undefined) return;

            scrollFrame = window.requestAnimationFrame(() => {
                scrollFrame = undefined;
                syncScrollTop();
            });
        };

        syncContainerSize();
        syncScrollTop();
        window.addEventListener("resize", resize);
        window.addEventListener("scroll", scroll, { passive: true });

        return () => {
            window.removeEventListener("resize", resize);
            window.removeEventListener("scroll", scroll);

            if (resizeFrame !== undefined) window.cancelAnimationFrame(resizeFrame);
            if (scrollFrame !== undefined) window.cancelAnimationFrame(scrollFrame);
        };
    }, []);

    const renderResult = store.renderResult;

    useLayoutEffect(() => {
        const tableElement = tableRef.current;

        if (!tableElement) return;

        const rowElements = Array.from(tableElement.querySelectorAll<HTMLElement>("tbody[data-order-index]"));
        const scheduleMeasure = (elements: HTMLElement[]) => {
            elements.forEach((element) => pendingMeasurementElementsRef.current.add(element));

            if (measureFrameRef.current !== undefined) {
                return;
            }

            measureFrameRef.current = window.requestAnimationFrame(() => {
                measureFrameRef.current = undefined;
                const elementsToMeasure = Array.from(pendingMeasurementElementsRef.current);
                pendingMeasurementElementsRef.current.clear();

                const measurements = elementsToMeasure.map((element) => ({
                    index: Number(element.dataset.orderIndex),
                    height: element.getBoundingClientRect().height,
                }));

                store.measureRows(measurements);
            });
        };

        // 首次挂载先批量测量；共享一个原生观察器，及时同步详情展开等行高变化。
        scheduleMeasure(rowElements);
        const resizeObserver = new ResizeObserver((entries) => {
            scheduleMeasure(entries.map((entry) => entry.target as HTMLElement));
        });
        rowElements.forEach((element) => resizeObserver.observe(element));

        return () => {
            resizeObserver.disconnect();
            pendingMeasurementElementsRef.current.clear();

            if (measureFrameRef.current !== undefined) {
                window.cancelAnimationFrame(measureFrameRef.current);
                measureFrameRef.current = undefined;
            }
        };
    }, [data, renderResult.start, renderResult.end, store]);

    return (
        <table className="table table-bordered order-table" ref={tableRef} style={{ overflowAnchor: "none" }}>
            {prefix}
            {thead}

            <tbody>
                <tr style={{ height: renderResult.marginTop }}></tr>
            </tbody>

            {renderResult.list.map((it) => (
                <RenderRow key={it.key} {...renderRowProps} index={it.index} data={it.data} />
            ))}

            <tbody>
                <tr style={{ height: renderResult.marginBottom }}></tr>
            </tbody>
        </table>
    );
};

export default VirtualizedTable;
